// We hack around URL parsing by using this single anchor. It gets used
// in a handful of the functions in the module. There's only one because
// some of these codepaths can be hot and we don't need to spam GC.
const anchor = document.createElement('a');

/**
 * Given a path, ensure the first character is a slash. This exists solely
 * because IE11 doesn't use leading slashes for the pathname property of
 * HTMLAnchorElements.
 *
 * @param {String} path A pathname
 * @return {String} A normalized pathname
 */
function ensureLeadingSlash(path: string) {
  return path.charAt(0) === '/' ? path : '/' + path;
}

/**
 * Another pathname normalization function. In some cases we don't care
 * about having trailing slashes.
 *
 * @param {String} path A pathname
 * @return {String} The pathname with no trailing slash
 */
function removeTrailingSlash(path: string) {
  return path === '/' ? path : path.replace(/\/$/, '');
}

/**
 * Given a URL, extract the pathname.
 *
 * @param {String} href A URL
 * @return {String} A normalized pathname
 */
function pathFromHref(href: string) {
  anchor.href = href;
  return ensureLeadingSlash(anchor.pathname);
}

/**
 * Given a URL, extract the search component.
 *
 * @param {String} href A URL
 * @param {String} The search string
 */
function searchFromHref(href: string) {
  anchor.href = href;
  return anchor.search;
}

/**
 * Given a search string, deserialize it into its object equivalent.
 *
 * @param {String} search A search string
 * @return {Object} The object representation of the search string
 */
function searchParamsFromSearchString(search: string) {
  const params: { [key: string]: string } = {};

  if (!search || typeof search !== 'string' || search === '?') {
    return params;
  }

  search
    .substr(1)
    .replace(
      /([^=]+)(=([^&]+)&?)?/g,
      function replacer(_: string, key: string, __: string, value: string) {
        try {
          // If a value is missing, fallback to an empty string.
          params[decodeURIComponent(key)] =
            value !== undefined ? decodeURIComponent(value) : '';
        } catch (e) {
          // In the event of a malformed URL, we don't want the app to break.
          // Whenever `decodeURIComponent` might fail (e.g. bad input), we will
          // skip a given key-value pair.
        }
        return '';
      },
    );

  return params;
}

/**
 * Returns whether or not an href is absolute. We don't care about doing
 * any protocol detection here as this code path only gets hit when we're
 * performing a navigation. This path-hacking is purely to support IE11.
 *
 * @param {String} href The destination address
 * @return {Boolean} Whether or not the path is absolute
 */
function isAbsolutePath(href: string) {
  return href.substr(0, 4) === 'http';
}

/**
 * Returns whether or not a link is hash-based. This is mostly for support
 * in patching the location to match that of Front Door or the fallback iframe.
 *
 * @param {String} href The destination address
 * @return {Boolean} Whether or not the href is a hash link
 */
function isHash(href: string) {
  return href.charAt(0) === '#';
}

/**
 * The PHP entry points responsible for handling incoming requests
 * have some logic to respond with 302s when:
 *
 *   * The request is not an XHR
 *   * The request method is GET
 *   * The query string is empty
 *   * The pathname does not end in a trailing slash
 *
 * When these conditions are met, the server will redirect the user
 * to the same URL, but with a trailing slash. This isn't really ideal,
 * but can account for it on the client.
 *
 * By following the same specification on the client, we can avoid
 * breakages that occur from navigating to a page and then refreshing.
 * Generally speaking, links in the application do not use trailing
 * slashes, so navigating to a page and subsequently refreshing would
 * result in an additional roundtrip–this breaks the `history.state`
 * contract, as a server-bound navigation (e.g. redirect) occurred.
 * We use `history.state` in the history sibling module in order to
 * better maintain an understanding of the history stack, and breaking
 * it results in some errant behavior.
 *
 * Anyway, this thing exists, and we need it.
 *
 * @param {String} href A URL
 * @return {String} A URL compatible with Mailchimp's serverside expectations
 */
function ensureTrailingSlashWhenSearchIsEmpty(href: string) {
  if (isHash(href)) {
    return href;
  }

  if (!isAbsolutePath(href)) {
    href = ensureLeadingSlash(href);
  }

  anchor.href = href;

  let pathname = ensureLeadingSlash(anchor.pathname);

  if (
    // Search is empty
    anchor.search === '' &&
    // The pathname does not have a trailing slash
    pathname.length > 1 &&
    pathname.charAt(pathname.length - 1) !== '/'
  ) {
    pathname = pathname + '/';
  }

  return pathname + anchor.search + anchor.hash;
}

export {
  ensureTrailingSlashWhenSearchIsEmpty,
  pathFromHref,
  removeTrailingSlash,
  searchFromHref,
  searchParamsFromSearchString,
};
